import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { IAuthContext, AuthContext } from '../../models/AuthContext';
import { agpAxiosClient } from '../../../Common/api/ABHub/ABHubTokenConfig';
import {
    getMsalInstance,
    agpLoginRequest,
} from '../../../Common/api/msalConfig';
import { useAuthentication } from '../../../Common/hooks/useAuthentication';
// Imported Components
import { HubApp } from '../../../HubApp';
import { UnauthorizedPage } from './UnauthorizedPage';
import { LoadingPage } from './LoadingPage';
import { hasAccountInfo, HubIdTokenClaims } from '../../util/authUtils';
import {
    CapabilityActionType,
    CapabilityDispatch,
} from '../../../Common/components/Capabilities/capabilityReducer';
import { getAuthorizedServiceIds } from '../../../Common/components/Capabilities/capabilityUtils';
import { CancelToken } from 'axios';
import {
    getUserProfileData,
    setUserProfileData,
} from '../../../Common/api/ABHub/UserProfileApi';
import { usePromise } from '../../../Common/hooks/usePromise';
import { sendMetric } from '../../../Common/api/ABHub/MetricsSendApi';

interface IAuthRequiredProps extends PropsWithChildren<any> {
    pageLoadTime: number;
}

export const AuthRequired: React.FC<IAuthRequiredProps> = (
    props: IAuthRequiredProps
) => {
    const msalInstance = getMsalInstance();
    const {
        account,
        error: authError,
        login,
    } = useAuthentication(msalInstance, agpLoginRequest, [agpAxiosClient]);

    const authErrorMessagesToIgnore = ['No matching state found in storage'];

    const capabilityDispatch = useContext(CapabilityDispatch);

    const isUserAuthorized: boolean = hasAccountInfo(account);
    const authErrorValue: Error | null = authError || null;
    // For service-oriented update
    const authorizedServiceIds = getAuthorizedServiceIds(
        isUserAuthorized,
        account
    );

    const [dbUpdated, setDbUpdated] = useState<boolean>(false);
    const [isUpdated, setIsUpdated] = useState<boolean>(false);
    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);

    const getUserProfile = useCallback(
        async (cancelToken?: CancelToken) => {
            if (hasAccountInfo(account) && account) {
                const upn =
                    (account.idTokenClaims as HubIdTokenClaims)?.upn ??
                    account.username;
                const alias = typeof upn === 'string' ? upn.split('@')[0] : '';
                const user = await getUserProfileData(alias, cancelToken);

                if (!user) {
                    const newUser = await setUserProfileData(
                        alias,
                        undefined,
                        cancelToken
                    );

                    return newUser;
                }

                return user;
            }
        },
        [account]
    );

    const [user, profileError, isLoaded, start] = usePromise(
        getUserProfile,
        false
    );

    // Login immediately; skip if there's an auth error (user must click Login to retry)
    useEffect(() => {
        if (!authError && !hasAccountInfo(account)) {
            login();
        }
    }, [account, authError, login]);

    // Set account in capabilities context, which will be used to determine tenting
    useEffect(() => {
        if (!authError && hasAccountInfo(account)) {
            capabilityDispatch({
                type: CapabilityActionType.setAgpUser,
                user: account,
            });
        }
    }, [account, authError, capabilityDispatch]);

    // emit metric for first time user visits page
    useEffect(() => {
        if (
            isFirstLoad &&
            hasAccountInfo(account) &&
            props.pageLoadTime !== -1
        ) {
            //emit metric for load time
            //emit metric for user visit
            const dimensions = {
                Browser: window.navigator.userAgent,
                BrowserVersion: window.navigator.appVersion,
                PortalVersion: process.env.REACT_APP_BUILD_VERSION,
                PageName: 'Home',
            };

            sendMetric(
                props.pageLoadTime,
                'PageVisit',
                'AzureBridgeHub',
                dimensions
            );
            setIsFirstLoad(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, props.pageLoadTime]);

    // Grab user profile data
    useEffect(() => {
        if (hasAccountInfo(account)) {
            start();
        }

        if (profileError) {
            console.error(profileError);
        }
    }, [account]);

    const logout = async () => {
        await msalInstance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
        });
    };

    const [authContext, setAuthContext] = useState<IAuthContext>({
        login: async () => {
            await login(true);
        },
        logout: logout,
        user: account,
        isUserAuthorized: isUserAuthorized,
        authorizedServiceIds: authorizedServiceIds,
        userProfile: user,
    });

    useEffect(() => {
        function fetchData() {
            setAuthContext({
                login: async () => {
                    await login(true);
                },
                logout: logout,
                user: account,
                isUserAuthorized: isUserAuthorized,
                authorizedServiceIds: authorizedServiceIds,
                userProfile: user,
            });
        }

        if (isLoaded) {
            fetchData();
            setIsUpdated(true);
        }
    }, [account, user, isLoaded]);

    // If UI sent updates to the database, update it immediately
    useEffect(() => {
        if (dbUpdated) {
            setIsUpdated(false);

            start();
            setDbUpdated(false);

            if (isLoaded) {
                setAuthContext({ ...authContext, userProfile: user });
                setIsUpdated(true);
            }
        }
    }, [dbUpdated, authContext, user, isLoaded]);

    return (
        <AuthContext.Provider value={{ authContext, setDbUpdated, isUpdated }}>
            {authErrorValue &&
            !authErrorMessagesToIgnore.includes(authErrorValue.message) ? (
                <UnauthorizedPage error={authErrorValue} />
            ) : isUserAuthorized ? (
                <HubApp />
            ) : (
                <LoadingPage />
            )}
        </AuthContext.Provider>
    );
};
